<header class="content-header">
  <h4 class="vision-h4">Create Tasks</h4>
  @if (data.project_id && includesTagsContainer) {
    <div class="project-context">
      <span test-id="project-context">{{ 'Project' | titlecase }}: {{ includesTagsContainer.label }}</span>
    </div>
  }
</header>

<div mat-dialog-content>
  <form class="create-group-form" [formGroup]="formGroup">
    @if (!data.project_id && currentUser) {
      <div class="task-criteria">
        <label aria-label="Select Project"> Select Project </label>
        <div class="mat-form-field">
          <project-picker
            #projectPicker
            test-id="project-picker"
            [filter]="getProjectsFilter()"
            [validators]="[requiredValidator, projectHasDataValidator]"
            (valueChange)="selectProject($event)"
          >
            @if (projectPicker.touched && projectPicker.errors?.required) {
              <mat-error> Project is required. </mat-error>
            }
            @if (projectPicker.touched && projectPicker.errors?.empty) {
              <mat-error>
                {{ projectPicker.errors.empty }}
              </mat-error>
            }
          </project-picker>
        </div>
      </div>
    }

    <div class="task-criteria">
      <label aria-label="Task Type"> Task Type </label>
      <mat-form-field appearance="outline">
        <mat-select formControlName="taskType" test-id="task-type">
          @for (taskType of taskTypes; track taskType) {
            <mat-option [value]="taskType.value">{{ taskType.label }}</mat-option>
          }
        </mat-select>
        @if (formGroup.controls.taskType.touched && formGroup.controls.taskType.hasError('required')) {
          <mat-error>{{ 'Task Type is required.' }}</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Read protocols only display for "Reader" task type -->
    @if (formGroup.controls.taskType.value === 'R' && protocolOptions) {
      <div class="task-criteria">
        <label aria-label="Read Protocol"> Read Protocol </label>
        <mat-form-field appearance="outline">
          <mat-select formControlName="readProtocol" placeholder="Select Read Protocol" test-id="read-protocol">
            @for (protocol of protocolOptions; track protocol) {
              <mat-option [value]="protocol.value">{{ protocol.label }}</mat-option>
            }
          </mat-select>
          @if (formGroup.controls.readProtocol.hasError('required')) {
            <mat-error>Read protocol is required.</mat-error>
          }
        </mat-form-field>
        @if (protocolOptions.length === 0) {
          <div class="read-protocol-warning">
            @if (getProjectLabel(formGroup.controls.project.value); as projectLabel) {
              <mat-icon class="xs orange">warning</mat-icon
              ><i
                >No read protocols were found for project labeled '{{ projectLabel }}'. See documentation for details to
                add a protocol for read tasks.</i
              >
            } @else {
              @if (projectWasTouched()) {
                <mat-icon class="xs orange">warning</mat-icon
                ><i>No read protocols were found. See documentation for details to add a protocol for read tasks.</i>
              }
            }
          </div>
        }
      </div>
    }

    <div class="task-criteria">
      <label aria-label="Container"> Container </label>
      <mat-form-field appearance="outline">
        <mat-select formControlName="container" placeholder="Select Container" test-id="fw-object-select">
          @for (container of containerTypes; track container) {
            <mat-option [value]="container.value">{{ container.label }}</mat-option>
          }
        </mat-select>
        @if (formGroup.controls.container.hasError('required')) {
          <mat-error>Container is required.</mat-error>
        }
      </mat-form-field>
    </div>

    @if (includesTagsContainer) {
      <div class="full-width">
        <label aria-label="Container Filter"> Container Filter </label>
        <div>
          <mat-label>Contains tag(s)</mat-label>
          <fw-tags
            formControlName="containerIncludesTags"
            test-id="container-includes-tags"
            [autoSave]="false"
            [container]="includesTagsContainer"
            [useAsInlineField]="true"
          />
        </div>
        <div>
          <mat-label>Does NOT contain tag(s) </mat-label>
          <fw-tags
            formControlName="containerExcludesTags"
            test-id="container-excludes-tags"
            [autoSave]="false"
            [container]="excludesTagsContainer"
            [useAsInlineField]="true"
          />
        </div>
      </div>
    }

    @if (shouldFetchContainerCount$ | async) {
      <div class="container-count full-width">
        @if (!loadingMatchingContainers) {
          <strong class="blue" style="font-size: 16px">{{ matchingContainerCount }}</strong>
          <span>{{ selectedContainerLabel }} found</span>
        } @else {
          <mat-spinner diameter="16" />
          <span>... searching for {{ selectedContainerLabel | lowercase }}</span>
        }
      </div>
    }

    <div class="task-criteria">
      <div class="flex-label">
        <label aria-label="Assignee"> Assignee </label>
        <mat-icon
          class="xs"
          fwTooltip="This list displays user with the correct permissions to perform Read Tasks in the system."
          >info</mat-icon
        >
      </div>
      <div class="mat-form-field">
        <app-typeahead
          #assigneeTypeahead
          class="solid-background full-width"
          placeholder="Assignee"
          test-id="user-typeahead"
          [disabled]="formGroup.get('assignee').disabled"
          [options]="userOptions"
          [validators]="[requiredAssigneeValidator, noAssigneesValidator]"
          (valueChange)="updateAssignee($event.toString())"
        />
      </div>
    </div>

    <div class="task-criteria">
      <label aria-label="Date Due"> Date Due </label>
      <mat-form-field appearance="outline" class="date-field flex-field">
        <input
          autocomplete="off"
          formControlName="dueDate"
          matInput
          placeholder="Select Date"
          test-id="due-date-input"
          [matDatepicker]="datepicker"
        />
        <mat-datepicker-toggle test-id="due-date-picker-toggle" [for]="datepicker" />
        <mat-datepicker #datepicker />
        @if (formGroup.controls.dueDate.hasError('required')) {
          <mat-error>Date Due is required.</mat-error>
        }
        @if (formGroup.controls.dueDate.hasError('dateIsPast')) {
          <mat-error>Date Due must be a future date.</mat-error>
        }
      </mat-form-field>
    </div>

    @if (taskTagsContainer) {
      <div class="full-width">
        <label>Task Tags</label>
        <fw-tags
          formControlName="tags"
          [allowCustomTags]="true"
          [autoSave]="false"
          [container]="taskTagsContainer"
          [useAsInlineField]="true"
        />
      </div>
    }
  </form>
</div>

<div align="end" class="action-buttons" mat-dialog-actions>
  <div class="footer-create-more">
    <mat-checkbox test-id="create-more-checkbox" [(ngModel)]="createMore" />
    <span>Create More Tasks</span>
  </div>
  <button mat-stroked-button test-id="close-button" (click)="cancel()">Cancel</button>
  <button color="primary" mat-flat-button test-id="create-button" [disabled]="formGroup.invalid" (click)="submit()">
    Create
  </button>
</div>
